import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { alpha, Box, Button, IconButton, Slider, Tooltip, Typography, useTheme } from '@mui/material';
import {
  CallEnd,
  Headphones,
  HeadsetOff,
  Mic,
  MicOff,
  PhoneDisabled,
  PhonePaused,
  PhoneInTalk,
  SettingsPhone,
} from '@mui/icons-material';
import { keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Janus, { type JanusJS } from 'janus-gateway';
import type { CallHistoryItem, CallSegmentType } from '../types';
import { ACTIVE_CALL_STATUSES, INTERMEDIARY_CALL_STATUSES } from '../constants/callStatusGroups';
import { SIP_INTERACTION_ALREADY_ANSWERED_ERROR } from '../constants/sipInteraction';
import { dispatchTelephonyIncomingResolved } from '../constants/telephonyEvents';
import { teardownMonitorTransport } from './call-control/monitorCleanup';
import { useSipExtension } from '../hooks/useSipExtension';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/errors';
import { describeJanusAuth, resolveJanusAuthOptions, type JanusAuthPayload } from '../utils/janusAuth';

import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';

type MonitorState = 'idle' | 'starting' | 'active' | 'stopping' | 'error';
type JanusPluginType = 'streaming';

interface MonitorIceCandidateErrorEvent extends Event {
  address?: string | null;
  port?: number | null;
  url?: string;
  errorCode?: number;
  errorText?: string;
}

interface MonitorRtcStats extends RTCStats {
  kind?: string;
  mediaType?: string;
  bytesReceived?: number;
  bytesSent?: number;
  packetsReceived?: number;
  jitter?: number;
  selectedCandidatePairId?: string;
  localCandidateId?: string;
  remoteCandidateId?: string;
  state?: string;
  nominated?: boolean;
  currentRoundTripTime?: number;
  totalRoundTripTime?: number;
  availableOutgoingBitrate?: number;
  availableIncomingBitrate?: number;
  candidateType?: string;
  protocol?: string;
  address?: string;
  port?: number;
  networkType?: string;
  dtlsState?: string;
  srtpCipher?: string;
  tlsVersion?: string;
}

interface MonitorPluginMessage {
  error?: unknown;
  plugindata?: {
    data?: {
      error?: unknown;
      error_reason?: unknown;
    };
  };
}

const SEGMENT_STATUS_MESSAGE_KEYS: Record<CallSegmentType, string> = {
  pregate: 'segmentInline.pregate',
  amd: 'segmentInline.amd',
  ai: 'segmentInline.ai',
  telephony_internal_extension: 'segmentInline.telephony_internal_extension',
  telephony_outbound_internal_extension: 'segmentInline.telephony_outbound_internal_extension',
  telephony_external_extension: 'segmentInline.telephony_external_extension',
  telephony_external_phone: 'segmentInline.telephony_external_phone',
  ivr: 'segmentInline.ivr',
};

function formatElapsedSeconds(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

const orbPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

const orbStatusPulse = keyframes`
  0% {
    opacity: 0.72;
    transform: translate(-50%, -50%) scale(0.96);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.04);
  }
  100% {
    opacity: 0.72;
    transform: translate(-50%, -50%) scale(0.96);
  }
`;

interface CallControlCardProps {
  call: CallHistoryItem;
  canControlMonitor?: boolean;
  canControlHangup?: boolean;
  canControlHold?: boolean;
  canControlMute?: boolean;
  showIncomingActions?: boolean;
  twoWayMode?: 'disabled' | 'on_mount' | 'on_accept';
  enableSipOrb?: boolean;
}

export default function CallControlCard({
  call,
  canControlMonitor = true,
  canControlHangup = true,
  canControlHold = false,
  canControlMute,
  showIncomingActions = false,
  twoWayMode = 'disabled',
  enableSipOrb = false,
}: CallControlCardProps) {
  const { t } = useTranslation(['components']);
  const theme = useTheme();
  const rawComponentId = useId();
  const componentId = useMemo(
    () => `call-control-${rawComponentId.replace(/:/g, '')}`,
    [rawComponentId],
  );
  const getElementId = (suffix: string) => `${componentId}-${suffix}`;
  const [hangupState, setHangupState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [hangupError, setHangupError] = useState<string | null>(null);
  const [holdState, setHoldState] = useState<'idle' | 'sending' | 'error'>('idle');
  const [holdError, setHoldError] = useState<string | null>(null);
  const [muteState, setMuteState] = useState<'idle' | 'sending' | 'error'>('idle');
  const [muteError, setMuteError] = useState<string | null>(null);
  const [isOnHold, setIsOnHold] = useState(Boolean(call.isOnHold));
  const [isCallMuted, setIsCallMuted] = useState(Boolean(call.isMuted));
  const [holdStartedAtLocal, setHoldStartedAtLocal] = useState<string | null>(call.holdStartedAt ?? null);
  const [mutedAtLocal, setMutedAtLocal] = useState<string | null>(call.mutedAt ?? null);
  const [monitorStartedAtLocal, setMonitorStartedAtLocal] = useState<string | null>(null);
  const [monitorState, setMonitorState] = useState<MonitorState>('idle');
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const [, setMonitorSessionId] = useState<string | null>(null);
  const [monitorAudioStream, setMonitorAudioStream] = useState<MediaStream | null>(null);
  const [monitorVolume, setMonitorVolume] = useState(0.9);
  const [monitorLevel, setMonitorLevel] = useState(0);
  const [sipOrbLevel, setSipOrbLevel] = useState(0);
  const [elapsedNowMs, setElapsedNowMs] = useState(() => Date.now());
  const [incomingActionSelected, setIncomingActionSelected] = useState(false);
  const janusRef = useRef<Janus | null>(null);
  const streamingHandleRef = useRef<JanusJS.PluginHandle | null>(null);
  const janusPluginRef = useRef<JanusPluginType | null>(null);
  const monitorSessionRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const monitorAnimationRef = useRef<number | null>(null);
  const sipOrbAnimationRef = useRef<number | null>(null);
  const sipOrbAudioContextRef = useRef<AudioContext | null>(null);
  const sipOrbAnalyserRef = useRef<AnalyserNode | null>(null);
  const keepaliveRef = useRef<number | null>(null);
  const statsIntervalRef = useRef<number | null>(null);
  const lastStatsRef = useRef<{ ts: number; bytes: number } | null>(null);
  const recoveringRef = useRef(false);
  const janusInitRef = useRef(false);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const pcDebugRef = useRef<RTCPeerConnection | null>(null);
  const outboundAutoAcceptAttemptedRef = useRef<string | null>(null);
  const cleanupMonitorHandlerRef = useRef<
    (options?: { resetRecovering?: boolean; skipHangup?: boolean }) => Promise<void>
  >(async () => undefined);
  const keepaliveMonitorBackendHandlerRef = useRef<(sessionId: string) => Promise<void>>(
    async () => undefined,
  );
  const logPeerStatsHandlerRef = useRef<() => Promise<void>>(async () => undefined);
  const stopMonitorBackendHandlerRef = useRef<(sessionId: string) => Promise<void>>(
    async () => undefined,
  );
  const stopMonitorHandlerRef = useRef<() => Promise<void>>(async () => undefined);
  const {
    status: sipStatus,
    incomingCall,
    acceptIncoming,
    rejectIncoming,
    hold: holdSip,
    unhold: unholdSip,
    mute: muteSip,
    unmute: unmuteSip,
    incomingRecoveryState,
    lastError: sipLastError,
    remoteAudioStream: sipRemoteAudioStream,
  } = useSipExtension();
  const { refreshBootstrap, user } = useAuth();
  const monitorDebugEnabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('callControlDebug') || localStorage.getItem('ct_call_control_debug') === '1';
  }, []);
  useEffect(() => {
    if (!monitorDebugEnabled) return;
    const params = new URLSearchParams(window.location.search);
    console.info('[Monitor]', 'debug enabled', {
      byQuery: params.has('callControlDebug'),
      byLocalStorage: localStorage.getItem('ct_call_control_debug') === '1',
    });
  }, [monitorDebugEnabled]);

  const controlCardSx = {
    position: 'relative',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    p: 2,
    minHeight: 240,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  };

  const shouldUseSipOrb = enableSipOrb && monitorState !== 'active';
  const sipHasRemoteAudio = shouldUseSipOrb && Boolean(sipRemoteAudioStream?.getAudioTracks().length);
  const orbLevel = monitorState === 'active'
    ? monitorLevel
    : (sipHasRemoteAudio ? sipOrbLevel : 0);
  const orbStyles = useMemo(() => {
    const clamped = Math.max(0, Math.min(1, orbLevel));
    const scale = 0.9 + clamped * 0.55;
    const glowOpacity = 0.12 + clamped * 0.7;
    const shimmerOpacity = 0.18 + clamped * 0.6;
    const pulseDuration = `${Math.max(2.6, 6 - clamped * 4)}s`;
    return { scale, glowOpacity, shimmerOpacity, pulseDuration };
  }, [orbLevel]);
  const hasRemoteAudio = monitorState === 'active'
    ? Boolean(monitorAudioStream?.getAudioTracks().length)
    : sipHasRemoteAudio;
  const hasMonitorConnection = hasRemoteAudio;
  const orbStatusIndicators = useMemo(() => {
    const indicators: Array<{
      key: 'hold' | 'monitor' | 'mute';
      icon: ReactElement;
      color: string;
      glowColor: string;
      label: string;
    }> = [];
    if (canControlMonitor && monitorState === 'active') {
      indicators.push({
        key: 'monitor',
        icon: <Headphones sx={{ fontSize: 24 }} />,
        color: theme.palette.info.main,
        glowColor: alpha(theme.palette.info.main, 0.34),
        label: t('components:callDetails.controls.monitor.statusActive'),
      });
    }
    if (isOnHold) {
      indicators.push({
        key: 'hold',
        icon: <PhoneInTalk sx={{ fontSize: 26 }} />,
        color: theme.palette.success.main,
        glowColor: alpha(theme.palette.success.main, 0.38),
        label: t('components:callDetails.controls.hold.resume'),
      });
    }
    if (isCallMuted) {
      indicators.push({
        key: 'mute',
        icon: <MicOff sx={{ fontSize: 24 }} />,
        color: theme.palette.warning.main,
        glowColor: alpha(theme.palette.warning.main, 0.34),
        label: t('components:callDetails.controls.mute.unmute'),
      });
    }
    return indicators;
  }, [
    canControlMonitor,
    isCallMuted,
    isOnHold,
    monitorState,
    t,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ]);
  const orbStatusItems = useMemo(() => {
    const activeStates: Array<{ key: 'hold' | 'monitor' | 'mute'; label: string; elapsedLabel: string | null }> = [];
    if (canControlMonitor && monitorState === 'active') {
      const startedAtMs = monitorStartedAtLocal ? Date.parse(monitorStartedAtLocal) : NaN;
      const elapsedLabel = Number.isFinite(startedAtMs)
        ? formatElapsedSeconds(Math.max(0, Math.floor((elapsedNowMs - startedAtMs) / 1000)))
        : null;
      activeStates.push({
        key: 'monitor',
        label: t('components:callDetails.controls.monitor.statusActive'),
        elapsedLabel,
      });
    }
    if (isOnHold) {
      const startedAtMs = holdStartedAtLocal ? Date.parse(holdStartedAtLocal) : NaN;
      const elapsedLabel = Number.isFinite(startedAtMs)
        ? formatElapsedSeconds(Math.max(0, Math.floor((elapsedNowMs - startedAtMs) / 1000)))
        : null;
      activeStates.push({
        key: 'hold',
        label: t('components:callDetails.controls.hold.statusActive'),
        elapsedLabel,
      });
    }
    if (isCallMuted) {
      const startedAtMs = mutedAtLocal ? Date.parse(mutedAtLocal) : NaN;
      const elapsedLabel = Number.isFinite(startedAtMs)
        ? formatElapsedSeconds(Math.max(0, Math.floor((elapsedNowMs - startedAtMs) / 1000)))
        : null;
      activeStates.push({
        key: 'mute',
        label: t('components:callDetails.controls.mute.statusActive'),
        elapsedLabel,
      });
    }
    return activeStates;
  }, [
    canControlMonitor,
    elapsedNowMs,
    holdStartedAtLocal,
    isCallMuted,
    isOnHold,
    monitorStartedAtLocal,
    monitorState,
    mutedAtLocal,
    t,
  ]);
  const openSegment = useMemo(() => {
    const segments = call.segments ?? [];
    return segments.find((segment) => !segment.endedAt) ?? null;
  }, [call.segments]);
  const activeTelephonySegmentType = openSegment?.segmentType ?? call.activeSegmentType ?? null;
  const isTelephonySegmentActive =
    activeTelephonySegmentType === 'telephony_internal_extension'
    || activeTelephonySegmentType === 'telephony_outbound_internal_extension'
    || activeTelephonySegmentType === 'telephony_external_extension'
    || activeTelephonySegmentType === 'telephony_external_phone';
  const isTelephonySegmentConnected = Boolean(
    openSegment?.connectedAt
    || call.telephonyTransferConnectedAt,
  );
  const normalizedTransferDestinationValue = (call.transferDestinationValue ?? '').trim();
  const normalizedUserExtension = (user?.telephonyEndpoint?.extension ?? '').trim();
  const userOwnsDestinationExtension = Boolean(
    normalizedTransferDestinationValue
    && normalizedUserExtension
    && (
      call.transferDestinationType === 'internal_extension'
      || call.transferDestinationType === 'external_extension'
    )
    && normalizedTransferDestinationValue === normalizedUserExtension
  );
  const isTelephonySegmentOwnedByCurrentUser = useMemo(() => {
    if (twoWayMode === 'on_mount') return true;
    if (!user?.id) return false;
    return Boolean(
      showIncomingActions
      || (
      (call.transferTargetUserId && call.transferTargetUserId === user.id)
      || userOwnsDestinationExtension
      )
    );
  }, [
    call.transferTargetUserId,
    showIncomingActions,
    twoWayMode,
    user?.id,
    userOwnsDestinationExtension,
  ]);
  const callStatusLabel = t(`components:statusChips.call.labels.${call.status}`, call.status);
  const isTerminalCall =
    Boolean(call.endedAt)
    || (
      !ACTIVE_CALL_STATUSES.includes(call.status as (typeof ACTIVE_CALL_STATUSES)[number])
      && !INTERMEDIARY_CALL_STATUSES.includes(call.status as (typeof INTERMEDIARY_CALL_STATUSES)[number])
    );
  const isCallClosed = isTerminalCall;
  const finalOutcomeLabel = useMemo(() => {
    if (!isTerminalCall) return null;
    const hasMismatch = call.isMismatch || call.ivrSelectedStatus === 'mismatch' || call.status === 'mismatch';
    if (hasMismatch) return t('components:callDetails.controls.callStatus.outcomes.mismatch');
    const hasOptout = call.isOptout || call.ivrSelectedStatus === 'optout';
    if (hasOptout) return t('components:callDetails.controls.callStatus.outcomes.optout');
    const hasConverted = call.isConverted || call.ivrSelectedStatus === 'converted';
    if (hasConverted) return t('components:callDetails.controls.callStatus.outcomes.converted');
    return null;
  }, [
    call.isConverted,
    call.isMismatch,
    call.isOptout,
    call.ivrSelectedStatus,
    call.status,
    isTerminalCall,
    t,
  ]);
  const callStartAt = call.initiatedAt ?? call.answeredAt ?? call.ringingAt ?? call.queuedAt ?? call.createdAt ?? null;
  const elapsedSeconds = useMemo(() => {
    if (!callStartAt) return 0;
    const startedAtMs = Date.parse(callStartAt);
    if (!Number.isFinite(startedAtMs)) return 0;
    const endedAtMs = call.endedAt ? Date.parse(call.endedAt) : NaN;
    if (Number.isFinite(endedAtMs)) {
      return Math.max(0, Math.floor((endedAtMs - startedAtMs) / 1000));
    }
    return Math.max(0, Math.floor((elapsedNowMs - startedAtMs) / 1000));
  }, [call.endedAt, callStartAt, elapsedNowMs]);
  const openSegmentStatus = activeTelephonySegmentType
    ? t(`components:callDetails.controls.callStatus.${SEGMENT_STATUS_MESSAGE_KEYS[activeTelephonySegmentType]}`)
    : null;
  const callStatusInlineText = [
    `${formatElapsedSeconds(elapsedSeconds)} ${callStatusLabel}`,
    finalOutcomeLabel,
    openSegmentStatus,
  ].filter(Boolean).join(' · ');

  const isMonitorActive = monitorState === 'active';
  const isMonitorBusy = monitorState === 'starting' || monitorState === 'stopping';
  const canUseMuteControl = canControlMute ?? canControlHold;
  const canToggleMonitor = canControlMonitor && !isMonitorBusy && !isCallClosed;
  const canTriggerHangup = canControlHangup && hangupState !== 'sending' && !isCallClosed;
  const canShowHoldAction =
    canControlHold
    && !isCallClosed
    && isTelephonySegmentActive
    && isTelephonySegmentConnected
    && isTelephonySegmentOwnedByCurrentUser;
  const canShowMuteAction =
    canUseMuteControl
    && !isCallClosed
    && isTelephonySegmentActive
    && isTelephonySegmentConnected
    && isTelephonySegmentOwnedByCurrentUser;
  const canToggleHold =
    canControlHold
    && canShowHoldAction
    && holdState !== 'sending'
    && !isCallClosed
    && !isCallMuted;
  const canToggleMute =
    canShowMuteAction
    && muteState !== 'sending'
    && !isCallClosed
    && !isOnHold;
  const incomingRejectReason = 'incoming_rejected_by_user';
  const isTransferGroupIncoming = Boolean(call.transferTargetGroupId);
  const isInteractionAnsweredByCurrentUser = Boolean(
    user?.id
    && call.interactionAnsweredById
    && call.interactionAnsweredById === user.id,
  );
  const isInteractionAnsweredByAnotherUser = Boolean(
    call.interactionAnsweredById
    && (!user?.id || call.interactionAnsweredById !== user.id),
  );
  const canRejectIncomingOffer = canTriggerHangup || isTransferGroupIncoming;
  const formatSipControlError = (error: string | null): string | null => {
    if (!error) return null;
    if (error === SIP_INTERACTION_ALREADY_ANSWERED_ERROR) {
      return t('components:callDetails.controls.incoming.answeredElsewhere');
    }
    return error;
  };
  const hasIncomingSupportedSegmentType =
    activeTelephonySegmentType === 'telephony_internal_extension'
    || activeTelephonySegmentType === 'telephony_external_extension';
  const hasPendingIncomingForCurrentCall = Boolean(
    sipStatus === 'incoming'
    && incomingCall
    && (!incomingCall.callPublicId || incomingCall.callPublicId === call.id),
  );
  const isIncomingTransferAwaitingAction = (
    showIncomingActions
    && hasIncomingSupportedSegmentType
    && hasPendingIncomingForCurrentCall
    && !isCallClosed
  );
  const shouldShowIncomingDecisionPanel = isIncomingTransferAwaitingAction && !incomingActionSelected;
  const canShowHoldActionUi = canShowHoldAction && !shouldShowIncomingDecisionPanel;
  const canShowMuteActionUi = canShowMuteAction && !shouldShowIncomingDecisionPanel;
  const canTriggerHangupUi = canTriggerHangup && !shouldShowIncomingDecisionPanel;
  const isIncomingRecoveryInProgress = incomingRecoveryState !== 'idle';
  const incomingRecoveryMessage = useMemo(() => {
    if (incomingRecoveryState === 'waiting_offer') {
      return t('components:callDetails.controls.incoming.waitingOffer');
    }
    if (incomingRecoveryState === 'retrying_delivery') {
      return t('components:callDetails.controls.incoming.retryingDelivery');
    }
    return null;
  }, [incomingRecoveryState, t]);
  const incomingTransferMessageKey = activeTelephonySegmentType === 'telephony_external_extension'
    ? 'components:callDetails.controls.callStatus.segmentMessages.telephony_external_extension'
    : 'components:callDetails.controls.callStatus.segmentMessages.telephony_internal_extension';
  useEffect(() => {
    if (sipLastError !== SIP_INTERACTION_ALREADY_ANSWERED_ERROR) return;
    if (isInteractionAnsweredByCurrentUser) {
      setMonitorError(null);
      return;
    }
    setMonitorError(t('components:callDetails.controls.incoming.answeredElsewhere'));
  }, [isInteractionAnsweredByCurrentUser, sipLastError, t]);
  useEffect(() => {
    if (!incomingActionSelected || !isTransferGroupIncoming || !isInteractionAnsweredByAnotherUser) return;
    setMonitorError(t('components:callDetails.controls.incoming.answeredElsewhere'));
  }, [
    incomingActionSelected,
    isInteractionAnsweredByAnotherUser,
    isTransferGroupIncoming,
    t,
  ]);
  useEffect(() => {
    if (!monitorDebugEnabled) return;
    console.info('[CallControlCard]', 'incoming controls evaluation', {
      callId: call.id,
      callStatus: call.status,
      callLifecycle: {
        endedAt: call.endedAt ?? null,
        answeredAt: call.answeredAt ?? null,
        telephonyTransferConnectedAt: call.telephonyTransferConnectedAt ?? null,
        transferDestinationType: call.transferDestinationType ?? null,
        transferDestinationValue: call.transferDestinationValue ?? null,
        transferTargetUserId: call.transferTargetUserId ?? null,
      },
      controlMode: {
        showIncomingActions,
        twoWayMode,
      },
      sipState: {
        status: sipStatus,
        incomingRecoveryState,
        hasIncomingCall: Boolean(incomingCall),
        incomingCallPublicId: incomingCall?.callPublicId ?? null,
        incomingCallSipCallId: incomingCall?.sipCallId ?? null,
        lastError: sipLastError,
      },
      segmentState: {
        activeSegmentType: call.activeSegmentType ?? null,
        openSegmentId: openSegment?.id ?? null,
        openSegmentType: openSegment?.segmentType ?? null,
        openSegmentConnectedAt: openSegment?.connectedAt ?? null,
        resolvedTelephonySegmentType: activeTelephonySegmentType,
      },
      expectedIncomingConditions: {
        showIncomingActionsMustBeTrue: true,
        supportedSegmentTypeMustBeTrue: true,
        sipMustBeIncomingForCurrentCall: true,
        callMustNotBeClosed: true,
        actionMustNotBeSelected: true,
      },
      currentIncomingConditions: {
        showIncomingActions,
        hasIncomingSupportedSegmentType,
        hasPendingIncomingForCurrentCall,
        isCallClosed,
        incomingActionSelected,
      },
      decision: {
        isIncomingTransferAwaitingAction,
        shouldShowIncomingDecisionPanel,
      },
      holdVisibilityConditions: {
        canControlHold,
        isTelephonySegmentActive,
        isTelephonySegmentConnected,
        isTelephonySegmentOwnedByCurrentUser,
        canShowHoldAction,
        canShowHoldActionUi,
        canToggleHold,
        isCallMuted,
      },
      muteVisibilityConditions: {
        isTelephonySegmentActive,
        isTelephonySegmentConnected,
        isTelephonySegmentOwnedByCurrentUser,
        canShowMuteAction,
        canShowMuteActionUi,
        canToggleMute,
        isOnHold,
      },
      hangupVisibilityConditions: {
        canControlHangup,
        canTriggerHangup,
        canTriggerHangupUi,
      },
    });
  }, [
    activeTelephonySegmentType,
    call.activeSegmentType,
    call.answeredAt,
    call.endedAt,
    call.id,
    call.status,
    call.telephonyTransferConnectedAt,
    call.transferDestinationType,
    call.transferDestinationValue,
    call.transferTargetUserId,
    canControlHold,
    canControlHangup,
    canShowHoldAction,
    canShowHoldActionUi,
    canShowMuteAction,
    canShowMuteActionUi,
    canToggleHold,
    canToggleMute,
    canTriggerHangup,
    canTriggerHangupUi,
    hasIncomingSupportedSegmentType,
    hasPendingIncomingForCurrentCall,
    incomingActionSelected,
    incomingCall,
    incomingRecoveryState,
    isCallClosed,
    isIncomingTransferAwaitingAction,
    isTelephonySegmentActive,
    isTelephonySegmentConnected,
    isTelephonySegmentOwnedByCurrentUser,
    isCallMuted,
    isOnHold,
    monitorDebugEnabled,
    openSegment,
    shouldShowIncomingDecisionPanel,
    showIncomingActions,
    sipLastError,
    sipStatus,
    twoWayMode,
  ]);

  const initJanus = () =>
    new Promise<void>((resolve) => {
      if (janusInitRef.current) {
        resolve();
        return;
      }
      Janus.init({
        debug: false,
        callback: () => {
          janusInitRef.current = true;
          resolve();
        },
      });
    });

  const cleanupMonitor = async (options?: { resetRecovering?: boolean; skipHangup?: boolean }) => {
    const resetRecovering = options?.resetRecovering ?? true;
    const skipHangup = options?.skipHangup ?? false;
    if (keepaliveRef.current) {
      window.clearInterval(keepaliveRef.current);
      keepaliveRef.current = null;
    }
    if (statsIntervalRef.current) {
      window.clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
    lastStatsRef.current = null;
    if (resetRecovering) {
      recoveringRef.current = false;
    }
    if (monitorAnimationRef.current) {
      cancelAnimationFrame(monitorAnimationRef.current);
      monitorAnimationRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    teardownMonitorTransport({
      clearMonitorAudioStream: () => setMonitorAudioStream(null),
      refs: {
        janusPluginRef,
        janusRef,
        pcDebugRef,
        remoteStreamRef,
        streamingHandleRef,
      },
      skipHangup,
    });
    setMonitorStartedAtLocal(null);
  };

  const getPeerConnection = () => streamingHandleRef.current?.webrtcStuff?.pc;

  const logPeerStats = async () => {
    const pc = getPeerConnection();
    if (!pc || typeof pc.getStats !== 'function') return;
    if (monitorDebugEnabled && pcDebugRef.current !== pc) {
      pcDebugRef.current = pc;
      pc.addEventListener('connectionstatechange', () => {
        console.info('[Monitor]', 'pc connectionstatechange', {
          state: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState,
        });
      });
      pc.addEventListener('iceconnectionstatechange', () => {
        console.info('[Monitor]', 'pc iceconnectionstatechange', {
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState,
        });
      });
      pc.addEventListener('icegatheringstatechange', () => {
        console.info('[Monitor]', 'pc icegatheringstatechange', {
          iceGatheringState: pc.iceGatheringState,
        });
      });
      pc.addEventListener('signalingstatechange', () => {
        console.info('[Monitor]', 'pc signalingstatechange', {
          signalingState: pc.signalingState,
        });
      });
      pc.addEventListener('icecandidateerror', (event: Event) => {
        const iceEvent = event as MonitorIceCandidateErrorEvent;
        console.info('[Monitor]', 'pc icecandidateerror', {
          address: iceEvent.address,
          port: iceEvent.port,
          url: iceEvent.url,
          errorCode: iceEvent.errorCode,
          errorText: iceEvent.errorText,
        });
      });
      console.info('[Monitor]', 'pc listeners attached');
    }
    try {
      const reports = await pc.getStats();
      const monitorReports: MonitorRtcStats[] = [];
      reports.forEach((report: RTCStats) => {
        monitorReports.push(report as MonitorRtcStats);
      });
      const inbound = monitorReports.find((report) => (
        report.type === 'inbound-rtp'
        && (report.kind === 'audio' || report.mediaType === 'audio')
      ));
      if (!inbound) return;
      const transport = monitorReports.find((report) => report.type === 'transport') ?? null;
      const candidatePairs = monitorReports.filter((report) => report.type === 'candidate-pair');
      const localCandidates = new Map(
        monitorReports
          .filter((report) => report.type === 'local-candidate')
          .map((report) => [report.id, report]),
      );
      const remoteCandidates = new Map(
        monitorReports
          .filter((report) => report.type === 'remote-candidate')
          .map((report) => [report.id, report]),
      );
      const bytes = Number(inbound.bytesReceived ?? 0);
      const packets = Number(inbound.packetsReceived ?? 0);
      const jitter = inbound.jitter ?? null;
      if (monitorDebugEnabled) {
        let selectedPair: MonitorRtcStats | null = null;
        if (transport?.selectedCandidatePairId) {
          selectedPair = candidatePairs.find(
            (pair) => pair.id === transport.selectedCandidatePairId,
          ) ?? null;
        }
        if (!selectedPair) {
          selectedPair = candidatePairs.find(
            (pair) => pair.state === 'succeeded' && pair.nominated,
          ) ?? null;
        }
        const local = selectedPair?.localCandidateId
          ? localCandidates.get(selectedPair.localCandidateId) ?? null
          : null;
        const remote = selectedPair?.remoteCandidateId
          ? remoteCandidates.get(selectedPair.remoteCandidateId) ?? null
          : null;
        console.info('[Monitor]', 'peer stats', {
          bytes,
          packets,
          jitter,
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState,
          selectedCandidatePair: selectedPair
            ? {
                id: selectedPair.id,
                state: selectedPair.state,
                nominated: selectedPair.nominated,
                currentRoundTripTime: selectedPair.currentRoundTripTime,
                totalRoundTripTime: selectedPair.totalRoundTripTime,
                availableOutgoingBitrate: selectedPair.availableOutgoingBitrate,
                availableIncomingBitrate: selectedPair.availableIncomingBitrate,
              }
            : null,
          localCandidate: local
            ? {
                candidateType: local.candidateType,
                protocol: local.protocol,
                address: local.address,
                port: local.port,
                networkType: local.networkType,
              }
            : null,
          remoteCandidate: remote
            ? {
                candidateType: remote.candidateType,
                protocol: remote.protocol,
                address: remote.address,
                port: remote.port,
              }
            : null,
          transport: transport
            ? {
                selectedCandidatePairId: transport.selectedCandidatePairId,
                dtlsState: transport.dtlsState,
                srtpCipher: transport.srtpCipher,
                tlsVersion: transport.tlsVersion,
                bytesSent: transport.bytesSent,
                bytesReceived: transport.bytesReceived,
              }
            : null,
        });
      }
      const now = Date.now();
      const last = lastStatsRef.current;
      if (last && bytes <= last.bytes && now - last.ts > 20000) {
        recoverMonitor('inbound_stalled', { bytes, packets, jitter }).catch(() => undefined);
      }
      lastStatsRef.current = { ts: now, bytes };
      if (pc.connectionState === 'failed') {
        recoverMonitor('pc_failed_stats', {
          bytes,
          packets,
          jitter,
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
        }).catch(() => undefined);
      }
    } catch {
      // no-op
    }
  };

  const recoverMonitor = async (reason: string, details?: Record<string, unknown>) => {
    if (recoveringRef.current) return;
    recoveringRef.current = true;
    if (monitorDebugEnabled) {
      console.info('[Monitor]', 'recover', { reason, details });
    }
    void reason;
    void details;
    try {
      if (monitorSessionRef.current) {
        await stopMonitorBackend(monitorSessionRef.current);
      }
    } catch {
      // no-op
    }
    await cleanupMonitor({ resetRecovering: false });
    setMonitorState('starting');
    setMonitorSessionId(null);
    monitorSessionRef.current = null;
    setTimeout(() => {
      startMonitor({
        force: true,
        reconnectionReason: reason,
      });
    }, 800);
  };

  const stopMonitorBackend = async (sessionId: string) => {
    const payload: Record<string, unknown> = {
      session_id: sessionId,
      mode: 'monitor',
    };
    await api.post(`/api/calls/${call.id}/session_stop/`, {
      ...payload,
    });
  };

  const keepaliveMonitorBackend = async (sessionId: string) => {
    const payload: Record<string, unknown> = {
      session_id: sessionId,
      mode: 'monitor',
    };
    await api.post(`/api/calls/${call.id}/session_keepalive/`, {
      ...payload,
    });
  };

  const formatMicrophoneError = (error: unknown): string => {
    const errorName = String((error as { name?: string })?.name || '');
    if (errorName === 'NotAllowedError') {
      return 'Microphone permission denied. Please allow microphone access to continue.';
    }
    if (errorName === 'NotFoundError') {
      return 'No microphone device found. Connect a microphone and try again.';
    }
    if (errorName === 'NotReadableError') {
      return 'Microphone is busy or unavailable. Close other apps using it and try again.';
    }
    return (error as { message?: string })?.message || 'Could not access microphone.';
  };

  const startMonitor = async (options?: {
    force?: boolean;
    reconnectionReason?: string;
  }) => {
    if (!canControlMonitor) return;
    if (!options?.force && (monitorState === 'starting' || monitorState === 'active')) return;
    setMonitorError(null);
    setMonitorState('starting');
    if (monitorDebugEnabled) {
      console.info('[Monitor]', 'start', { callId: call.id });
    }
    try {
      const payload: Record<string, unknown> = { mode: 'monitor' };
      if (options?.reconnectionReason) {
        payload.is_reconnection = true;
        payload.reconnection_reason = options.reconnectionReason;
      }
      const response = await api.post(`/api/calls/${call.id}/session_start/`, payload);

      const data = response.data as {
        sessionId: string;
        startedAt?: string | null;
        token: string;
        expiresAt: string;
        janusPlugin?: JanusPluginType;
        janusWsUrl: string;
        janusStreamId?: number | null;
        iceServers?: RTCIceServer[];
      } & JanusAuthPayload;

      monitorSessionRef.current = data.sessionId;
      setMonitorSessionId(data.sessionId);
      setMonitorStartedAtLocal(data.startedAt ?? new Date().toISOString());
      recoveringRef.current = false;
      if (monitorDebugEnabled) {
        console.info('[Monitor]', 'start response', {
          sessionId: data.sessionId,
          startedAt: data.startedAt,
          expiresAt: data.expiresAt,
          janusWsUrl: data.janusWsUrl,
          janusStreamId: data.janusStreamId,
          janusAuth: describeJanusAuth(data),
          iceServers: data.iceServers,
        });
      }

      await initJanus();

      janusRef.current = new Janus({
        server: data.janusWsUrl,
        ...resolveJanusAuthOptions(data),
        iceServers: data.iceServers || [],
        iceTransportPolicy: 'relay',
        success: () => {
          if (monitorDebugEnabled) {
            console.info('[Monitor]', 'janus init success');
          }
          const janusPlugin: JanusPluginType = data.janusPlugin || 'streaming';
          janusRef.current?.attach({
            plugin: 'janus.plugin.streaming',
            success: (pluginHandle: JanusJS.PluginHandle) => {
              streamingHandleRef.current = pluginHandle;
              janusPluginRef.current = janusPlugin;
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'plugin attached', {
                  plugin: 'janus.plugin.streaming',
                  streamId: data.janusStreamId,
                });
              }
              if (!data.janusStreamId) {
                setMonitorError('Monitor stream is not available.');
                setMonitorState('error');
                return;
              }
              pluginHandle.send({ message: { request: 'watch', id: data.janusStreamId } });
            },
            error: (error: unknown) => {
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'plugin error', { error });
              }
              setMonitorError(String(error));
              setMonitorState('error');
            },
            onmessage: (_msg: MonitorPluginMessage, jsep?: JanusJS.JSEP) => {
              const directErrorReason = (
                _msg.error !== null
                && typeof _msg.error === 'object'
                && 'reason' in _msg.error
              )
                ? (_msg.error as { reason?: unknown }).reason
                : null;
              const errorMessage =
                directErrorReason
                || _msg.error
                || _msg.plugindata?.data?.error
                || _msg.plugindata?.data?.error_reason;
              if (errorMessage) {
                if (monitorDebugEnabled) {
                  console.info('[Monitor]', 'janus message error', { errorMessage, msg: _msg });
                }
                setMonitorError(String(errorMessage));
                setMonitorState('error');
                return;
              }
              if (jsep) {
                if (monitorDebugEnabled) {
                  console.info('[Monitor]', 'received jsep', { type: jsep.type });
                }
                const streamingHandle = streamingHandleRef.current;
                if (!streamingHandle) {
                  setMonitorError('Monitor plugin is not attached.');
                  setMonitorState('error');
                  return;
                }
                streamingHandle.createAnswer({
                  jsep,
                  tracks: [{ type: 'audio', capture: false, recv: true }],
                  success: (localJsep: JanusJS.JSEP) => {
                    if (monitorDebugEnabled) {
                      console.info('[Monitor]', 'created answer', { type: localJsep?.type });
                    }
                    streamingHandle.send({
                      message: { request: 'start' },
                      jsep: localJsep,
                    });
                  },
                  error: (error: unknown) => {
                    if (monitorDebugEnabled) {
                      console.info('[Monitor]', 'createAnswer error', { error });
                    }
                    setMonitorError(String(error));
                    setMonitorState('error');
                  },
                });
              }
            },
            onwebrtcup: () => {
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'webrtc up');
              }
              setMonitorState('active');
            },
            onremotestream: (stream: MediaStream) => {
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'remote stream', {
                  tracks: stream.getTracks().map((track) => track.kind),
                });
              }
              setMonitorAudioStream(stream);
              setMonitorState('active');
            },
            onremotetrack: (track: MediaStreamTrack, _mid: string, on: boolean) => {
              if (track.kind !== 'audio') return;
              if (!remoteStreamRef.current) {
                remoteStreamRef.current = new MediaStream();
              }
              if (on) {
                remoteStreamRef.current.addTrack(track);
              } else {
                remoteStreamRef.current.removeTrack(track);
              }
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'remote track', {
                  kind: track.kind,
                  on,
                  totalTracks: remoteStreamRef.current.getTracks().length,
                });
              }
              setMonitorAudioStream(remoteStreamRef.current);
              setMonitorState('active');
            },
            oncleanup: () => {
              cleanupMonitor({ resetRecovering: false, skipHangup: true }).catch(() => undefined);
              setMonitorState(recoveringRef.current ? 'starting' : 'idle');
              setMonitorSessionId(null);
              monitorSessionRef.current = null;
            },
            onconnectionstate: (state: string) => {
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'pc connection state', { state });
              }
              if (state === 'failed') {
                recoverMonitor('pc_failed', { state }).catch(() => undefined);
              }
            },
            onicecandidate: (candidate: unknown) => {
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'ice candidate', { candidate });
              }
            },
            oniceconnectionstate: (state: string) => {
              if (monitorDebugEnabled) {
                console.info('[Monitor]', 'ice connection state', { state });
              }
              if (state === 'failed') {
                recoverMonitor('ice_failed', { state }).catch(() => undefined);
              }
            },
          } as unknown as JanusJS.PluginOptions);
        },
        error: (error: unknown) => {
          if (monitorDebugEnabled) {
            console.info('[Monitor]', 'janus init error', { error });
          }
          setMonitorError(String(error));
          setMonitorState('error');
        },
      });
    } catch (error: unknown) {
      if (monitorDebugEnabled) {
        console.info('[Monitor]', 'start error', { error });
      }
      setMonitorError(
        getApiErrorMessage(error, formatMicrophoneError(error) || 'Monitor start failed'),
      );
      setMonitorState('error');
      try {
        await cleanupMonitor();
      } catch {
        // no-op
      }
      recoveringRef.current = false;
    }
  };


  const stopMonitor = async () => {
    if (!canControlMonitor) return;
    if (!monitorSessionRef.current || monitorState === 'stopping') return;
    setMonitorState('stopping');
    setMonitorError(null);
    if (monitorDebugEnabled) {
      console.info('[Monitor]', 'stop', { sessionId: monitorSessionRef.current });
    }
    try {
      await stopMonitorBackend(monitorSessionRef.current);
    } catch (error: unknown) {
      setMonitorError(getApiErrorMessage(error, 'Monitor stop failed'));
    } finally {
      await cleanupMonitor();
      setMonitorState('idle');
      setMonitorSessionId(null);
      monitorSessionRef.current = null;
    }
  };

  const handleMonitorToggle = () => {
    if (!canToggleMonitor) return;
    if (isMonitorActive) {
      stopMonitor().catch(() => undefined);
      return;
    }
    startMonitor().catch(() => undefined);
  };

  const handleHangupOpen = () => {
    if (!canTriggerHangup) return;
    void performHangup('');
  };

  const performHangup = async (reason: string): Promise<boolean> => {
    setHangupState('sending');
    setHangupError(null);
    try {
      const normalizedReason = reason.trim();
      const response = await api.post(
        `/api/calls/${call.id}/hangup/`,
        normalizedReason ? { reason: normalizedReason } : {},
      );
      if (isMonitorActive) {
        await cleanupMonitor({ resetRecovering: false, skipHangup: true });
        setMonitorState('idle');
        setMonitorSessionId(null);
        monitorSessionRef.current = null;
      }
      if (response?.data?.requiredAction) {
        await refreshBootstrap();
      }
      setHangupState('success');
      return true;
    } catch (error: unknown) {
      setHangupState('error');
      setHangupError(getApiErrorMessage(error, t('components:callDetails.controls.hangup.error')));
      return false;
    }
  };

  const handleIncomingReject = async () => {
    if (!canRejectIncomingOffer) return;
    setIncomingActionSelected(true);
    dispatchTelephonyIncomingResolved({ callId: call.id, action: 'reject' });
    const rejected = await rejectIncoming();
    if (!rejected) {
      setMonitorError(
        formatSipControlError(sipLastError)
        || t('components:callDetails.controls.incoming.rejectFailed'),
      );
      return;
    }
    if (isTransferGroupIncoming) {
      if (isInteractionAnsweredByAnotherUser) {
        setMonitorError(t('components:callDetails.controls.incoming.answeredElsewhere'));
      }
      return;
    }
    await performHangup(incomingRejectReason);
  };

  const handleIncomingAccept = () => {
    (async () => {
      setMonitorError(null);
      setIncomingActionSelected(true);
      const accepted = await acceptIncoming(call.id);
      if (!accepted) {
        setIncomingActionSelected(false);
        setMonitorError(
          formatSipControlError(sipLastError)
          || t('components:callDetails.controls.incoming.acceptFailed', 'Could not accept incoming call.'),
        );
        return;
      }
      dispatchTelephonyIncomingResolved({ callId: call.id, action: 'accept' });
    })();
  };

  const handleHoldToggle = async () => {
    if (!canToggleHold) return;
    setHoldState('sending');
    setHoldError(null);
    const currentlyOnHold = isOnHold;
    try {
      const endpoint = currentlyOnHold ? 'unhold' : 'hold';
      const response = await api.post(`/api/calls/${call.id}/${endpoint}/`);
      const responseTransport = String(response?.data?.transport || '').trim().toLowerCase();
      if (responseTransport === 'sip') {
        const responseHoldDirection = String(response?.data?.hold_direction || '').trim().toLowerCase();
        const sipHoldDirection = responseHoldDirection === 'sendonly'
          || responseHoldDirection === 'recvonly'
          || responseHoldDirection === 'inactive'
          ? responseHoldDirection
          : 'recvonly';
        const sipResult = currentlyOnHold ? await unholdSip() : await holdSip(sipHoldDirection);
        if (!sipResult) {
          setHoldState('error');
          setHoldError(
            sipLastError
            || (currentlyOnHold
              ? t('components:callDetails.controls.hold.unholdError')
              : t('components:callDetails.controls.hold.holdError')),
          );
          return;
        }
        try {
          const syncResponse = await api.post(`/api/calls/${call.id}/${endpoint}/`, {
            syncOnly: true,
            transport: 'sip',
          });
          setIsOnHold(Boolean(syncResponse?.data?.isOnHold));
          setHoldStartedAtLocal(syncResponse?.data?.holdStartedAt ?? new Date().toISOString());
          setHoldState('idle');
          return;
        } catch (syncError: unknown) {
          if (currentlyOnHold) {
            await holdSip(sipHoldDirection);
          } else {
            await unholdSip();
          }
          throw syncError;
        }
      }
      if (typeof response?.data?.isOnHold === 'boolean') {
        setIsOnHold(response.data.isOnHold);
        setHoldStartedAtLocal(response.data.holdStartedAt ?? (response.data.isOnHold ? new Date().toISOString() : null));
      } else {
        const statusValue = response?.data?.status;
        setIsOnHold(statusValue === 'on_hold');
        setHoldStartedAtLocal(statusValue === 'on_hold' ? new Date().toISOString() : null);
      }
      setHoldState('idle');
    } catch (error: unknown) {
      setHoldState('error');
      setHoldError(
        getApiErrorMessage(
          error,
          currentlyOnHold
          ? t('components:callDetails.controls.hold.unholdError')
          : t('components:callDetails.controls.hold.holdError'),
          ['detail', 'message', 'error'],
        ),
      );
    }
  };

  const handleMuteToggle = async () => {
    if (!canToggleMute) return;
    setMuteState('sending');
    setMuteError(null);
    const currentlyMuted = isCallMuted;
    try {
      const sipResult = currentlyMuted ? await unmuteSip() : await muteSip();
      if (!sipResult) {
        setMuteState('error');
        setMuteError(
          sipLastError
          || (currentlyMuted
            ? t('components:callDetails.controls.mute.unmuteError')
            : t('components:callDetails.controls.mute.muteError')),
        );
        return;
      }
      try {
        const endpoint = currentlyMuted ? 'unmute' : 'mute';
        const response = await api.post(`/api/calls/${call.id}/${endpoint}/`);
        if (typeof response?.data?.isMuted === 'boolean') {
          setIsCallMuted(response.data.isMuted);
          setMutedAtLocal(response.data.mutedAt ?? (response.data.isMuted ? new Date().toISOString() : null));
        } else {
          setIsCallMuted(!currentlyMuted);
          setMutedAtLocal(!currentlyMuted ? new Date().toISOString() : null);
        }
        setMuteState('idle');
        return;
      } catch (syncError: unknown) {
        if (currentlyMuted) {
          await muteSip();
        } else {
          await unmuteSip();
        }
        throw syncError;
      }
    } catch (error: unknown) {
      setMuteState('error');
      setMuteError(
        getApiErrorMessage(
          error,
          currentlyMuted
          ? t('components:callDetails.controls.mute.unmuteError')
          : t('components:callDetails.controls.mute.muteError'),
          ['detail', 'message', 'error'],
        ),
      );
    }
  };

  cleanupMonitorHandlerRef.current = cleanupMonitor;
  keepaliveMonitorBackendHandlerRef.current = keepaliveMonitorBackend;
  logPeerStatsHandlerRef.current = logPeerStats;
  stopMonitorBackendHandlerRef.current = stopMonitorBackend;
  stopMonitorHandlerRef.current = stopMonitor;

  const renderHangupControl = () => {
    if (!canControlHangup) return null;
    return (
      <Box
        id={getElementId('hangup-section')}
        sx={{
          mt: 0,
          position: 'absolute',
          bottom: '12px',
          left: '16px',
          right: '16px',
          width: 'calc(100% - 40px)',
          zIndex: 10,
          bgcolor: '#1c1c29',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {hangupError && (
          <Typography id={getElementId('hangup-error')} variant="body2" color="error">
            {hangupError}
          </Typography>
        )}
        {hangupState === 'success' && (
          <Typography id={getElementId('hangup-success')} variant="body2" color="success.main">
            {t('components:callDetails.controls.hangup.success')}
          </Typography>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (!monitorAudioStream || !audioRef.current) return;
    if (monitorAnimationRef.current) {
      cancelAnimationFrame(monitorAnimationRef.current);
      monitorAnimationRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }

    const audioTracks = monitorAudioStream.getAudioTracks();
    if (audioTracks.length === 0) {
      if (monitorDebugEnabled) {
        console.info('[Monitor]', 'no audio tracks');
      }
      audioRef.current.srcObject = null;
      setMonitorLevel(0);
      if (recoveringRef.current) {
        cleanupMonitor({ resetRecovering: false }).catch(() => undefined);
        setMonitorState('starting');
        setMonitorSessionId(null);
        monitorSessionRef.current = null;
      } else {
        cleanupMonitor().catch(() => undefined);
        setMonitorState('idle');
        setMonitorSessionId(null);
        monitorSessionRef.current = null;
      }
      return;
    }

    audioRef.current.srcObject = monitorAudioStream;
    audioRef.current.volume = monitorVolume;
    audioRef.current.play().catch((error) => {
      if (monitorDebugEnabled) {
        console.info('[Monitor]', 'audio play error', { error });
      }
    });

    audioContextRef.current = new AudioContext();
    const source = audioContextRef.current.createMediaStreamSource(monitorAudioStream);
    const analyser = audioContextRef.current.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const updateLevel = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < bufferLength; i += 1) {
        const value = (dataArray[i] - 128) / 128;
        sum += value * value;
      }
      const rms = Math.sqrt(sum / bufferLength);
      setMonitorLevel(Math.min(1, rms * 4));
      monitorAnimationRef.current = requestAnimationFrame(updateLevel);
    };
    updateLevel();

    return () => {
      if (monitorAnimationRef.current) {
        cancelAnimationFrame(monitorAnimationRef.current);
        monitorAnimationRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
    };
  }, [monitorAudioStream, monitorDebugEnabled, monitorVolume]);

  useEffect(() => {
    const stopSipOrbAnalyzer = () => {
      if (sipOrbAnimationRef.current) {
        cancelAnimationFrame(sipOrbAnimationRef.current);
        sipOrbAnimationRef.current = null;
      }
      if (sipOrbAnalyserRef.current) {
        sipOrbAnalyserRef.current.disconnect();
        sipOrbAnalyserRef.current = null;
      }
      if (sipOrbAudioContextRef.current) {
        sipOrbAudioContextRef.current.close().catch(() => undefined);
        sipOrbAudioContextRef.current = null;
      }
    };

    stopSipOrbAnalyzer();
    if (!enableSipOrb || monitorState === 'active') {
      setSipOrbLevel(0);
      return () => {
        stopSipOrbAnalyzer();
      };
    }

    const stream = sipRemoteAudioStream;
    const audioTracks = stream?.getAudioTracks() ?? [];
    if (!stream || audioTracks.length === 0) {
      setSipOrbLevel(0);
      return () => {
        stopSipOrbAnalyzer();
      };
    }

    try {
      sipOrbAudioContextRef.current = new AudioContext();
      const source = sipOrbAudioContextRef.current.createMediaStreamSource(stream);
      const analyser = sipOrbAudioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      sipOrbAnalyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const updateLevel = () => {
        if (!sipOrbAnalyserRef.current) return;
        sipOrbAnalyserRef.current.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i += 1) {
          const value = (dataArray[i] - 128) / 128;
          sum += value * value;
        }
        const rms = Math.sqrt(sum / bufferLength);
        setSipOrbLevel(Math.min(1, rms * 4));
        sipOrbAnimationRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch {
      setSipOrbLevel(0);
    }

    return () => {
      stopSipOrbAnalyzer();
      setSipOrbLevel(0);
    };
  }, [enableSipOrb, monitorState, sipRemoteAudioStream]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = monitorVolume;
    }
  }, [monitorVolume]);

  useEffect(() => {
    if (!monitorSessionRef.current) return;
    if (monitorState === 'idle' || monitorState === 'stopping' || monitorState === 'error') {
      if (keepaliveRef.current) {
        window.clearInterval(keepaliveRef.current);
        keepaliveRef.current = null;
      }
      return;
    }
    if (keepaliveRef.current) return;
    const sessionId = monitorSessionRef.current;
    const runKeepalive = () => {
      keepaliveMonitorBackendHandlerRef.current(sessionId)
        .then(() => {
          if (monitorDebugEnabled) {
            console.info('[Monitor]', 'keepalive ok', { sessionId });
          }
        })
        .catch((error) => {
          if (monitorDebugEnabled) {
            console.info('[Monitor]', 'keepalive error', { sessionId, error });
          }
        });
    };
    runKeepalive();
    keepaliveRef.current = window.setInterval(runKeepalive, 20000);
    return () => {
      if (keepaliveRef.current) {
        window.clearInterval(keepaliveRef.current);
        keepaliveRef.current = null;
      }
    };
  }, [monitorDebugEnabled, monitorState]);

  useEffect(() => {
    if (monitorState !== 'active') {
      if (statsIntervalRef.current) {
        window.clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
      lastStatsRef.current = null;
      return;
    }
    if (statsIntervalRef.current) return;
    logPeerStatsHandlerRef.current().catch(() => undefined);
    statsIntervalRef.current = window.setInterval(() => {
      logPeerStatsHandlerRef.current().catch(() => undefined);
    }, 10000);
    return () => {
      if (statsIntervalRef.current) {
        window.clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
    };
  }, [monitorState]);

  useEffect(() => {
    return () => {
      if (monitorSessionRef.current) {
        stopMonitorBackendHandlerRef.current(monitorSessionRef.current).catch(() => undefined);
      }
      cleanupMonitorHandlerRef.current().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    if (call.endedAt) return;
    const intervalId = window.setInterval(() => setElapsedNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [call.endedAt]);

  useEffect(() => {
    if (!isCallClosed) return;
    if (!monitorSessionRef.current) return;
    if (monitorState !== 'active' && monitorState !== 'starting') return;
    stopMonitorHandlerRef.current().catch(() => undefined);
  }, [isCallClosed, monitorState]);

  useEffect(() => {
    setIncomingActionSelected(false);
    outboundAutoAcceptAttemptedRef.current = null;
    setHoldState('idle');
    setHoldError(null);
    setMuteState('idle');
    setMuteError(null);
    setMonitorStartedAtLocal(null);
  }, [call.id]);

  useEffect(() => {
    setIsOnHold(Boolean(call.isOnHold));
  }, [call.id, call.isOnHold]);

  useEffect(() => {
    setIsCallMuted(Boolean(call.isMuted));
  }, [call.id, call.isMuted]);

  useEffect(() => {
    setHoldStartedAtLocal(call.holdStartedAt ?? null);
  }, [call.holdStartedAt, call.id]);

  useEffect(() => {
    setMutedAtLocal(call.mutedAt ?? null);
  }, [call.id, call.mutedAt]);

  useEffect(() => {
    if (twoWayMode !== 'on_mount' || isCallClosed) return;
    if (outboundAutoAcceptAttemptedRef.current === call.id) return;
    outboundAutoAcceptAttemptedRef.current = call.id;
    let cancelled = false;
    (async () => {
      try {
        const accepted = await acceptIncoming(call.id, {
          retryIncomingDelivery: false,
          deferUntilOffer: true,
        });
        if (!accepted && !cancelled) {
          setMonitorError(sipLastError || 'Could not initialize SIP interaction for outbound call.');
        }
      } catch {
        if (cancelled) return;
        setMonitorError('Could not initialize SIP interaction for outbound call.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [acceptIncoming, call.id, isCallClosed, sipLastError, twoWayMode]);

  return (
    <Box id={getElementId('root')} sx={controlCardSx}>
      {(canControlMonitor || canTriggerHangupUi || canShowHoldActionUi || canShowMuteActionUi) && (
        <Box
          id={getElementId('actions-bar')}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 1,
            zIndex: theme.zIndex.modal + 1,
            pointerEvents: 'auto',
            bgcolor: alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2,
            p: 0.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {canShowMuteActionUi && (
            <Tooltip
              id={getElementId('mute-toggle-tooltip')}
              title={isCallMuted
                ? t('components:callDetails.controls.mute.unmute')
                : t('components:callDetails.controls.mute.mute')}
              arrow
            >
              <span id={getElementId('mute-toggle-wrapper')}>
                <IconButton
                  id={getElementId('mute-toggle-button')}
                  color={isCallMuted ? 'warning' : 'primary'}
                  onClick={handleMuteToggle}
                  disabled={!canToggleMute}
                  aria-label={isCallMuted
                    ? t('components:callDetails.controls.mute.unmute')
                    : t('components:callDetails.controls.mute.mute')}
                  sx={{ width: 44, height: 44 }}
                >
                  {isCallMuted ? <MicOff /> : <Mic />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canShowHoldActionUi && (
            <Tooltip
              id={getElementId('hold-toggle-tooltip')}
              title={isOnHold
                ? t('components:callDetails.controls.hold.resume')
                : t('components:callDetails.controls.hold.putOnHold')}
              arrow
            >
              <span id={getElementId('hold-toggle-wrapper')}>
                <IconButton
                  id={getElementId('hold-toggle-button')}
                  color={isOnHold ? 'success' : 'warning'}
                  onClick={handleHoldToggle}
                  disabled={!canToggleHold}
                  aria-label={isOnHold
                    ? t('components:callDetails.controls.hold.resume')
                    : t('components:callDetails.controls.hold.putOnHold')}
                  sx={{ width: 44, height: 44 }}
                >
                  {isOnHold ? <PhoneInTalk /> : <PhonePaused />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canControlMonitor && (
            <Tooltip
              id={getElementId('monitor-toggle-tooltip')}
              title={isMonitorActive
                ? t('components:callDetails.controls.monitor.stop')
                : t('components:callDetails.controls.monitor.start')}
              arrow
            >
              <span id={getElementId('monitor-toggle-wrapper')}>
                <IconButton
                  id={getElementId('monitor-toggle-button')}
                  color={isMonitorActive ? 'error' : 'primary'}
                  onClick={handleMonitorToggle}
                  disabled={!canToggleMonitor}
                  aria-label={isMonitorActive
                    ? t('components:callDetails.controls.monitor.stop')
                    : t('components:callDetails.controls.monitor.start')}
                  sx={{ width: 44, height: 44 }}
                >
                  {isMonitorActive
                    ? <HeadsetOff id={getElementId('monitor-stop-icon')} />
                    : <Headphones id={getElementId('monitor-start-icon')} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canTriggerHangupUi && (
            <Tooltip id={getElementId('hangup-open-tooltip')} title={t('components:callDetails.controls.hangup.confirm')} arrow>
              <span id={getElementId('hangup-open-wrapper')}>
                <IconButton
                  id={getElementId('hangup-open-button')}
                  color="error"
                  onClick={handleHangupOpen}
                  disabled={!canTriggerHangup}
                  aria-label={t('components:callDetails.controls.hangup.confirm')}
                  sx={{ width: 44, height: 44 }}
                >
                  <CallEnd id={getElementId('hangup-open-icon')} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      <Box id={getElementId('header')} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: 12 }}>
        <SettingsPhone id={getElementId('header-icon')} color="primary" />
        <Typography id={getElementId('header-title')} variant="h6" fontWeight={600}>
          {t('components:callDetails.controls.title')}
        </Typography>
      </Box>

      <Box
        id={getElementId('content')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          position: 'absolute',
          width: '100%',
          m: 'auto',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        }}
      >
        {monitorError && (
          <Typography id={getElementId('monitor-error')} variant="body2" color="error">
            {monitorError}
          </Typography>
        )}
        {holdError && (
          <Typography id={getElementId('hold-error')} variant="body2" color="error">
            {holdError}
          </Typography>
        )}
        {muteError && (
          <Typography id={getElementId('mute-error')} variant="body2" color="error">
            {muteError}
          </Typography>
        )}
        {incomingRecoveryMessage && (
          <Typography id={getElementId('incoming-recovery-message')} variant="body2" color="text.secondary">
            {incomingRecoveryMessage}
          </Typography>
        )}
        {shouldShowIncomingDecisionPanel ? (
          <Box
            id={getElementId('incoming-actions')}
            sx={{
              mt: 'auto',
              mb: 'auto',
              mx: 'auto',
              width: '100%',
              maxWidth: 320,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.96),
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t(incomingTransferMessageKey)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                id={getElementId('incoming-accept-button')}
                variant="contained"
                color="success"
                size="small"
                startIcon={<PhoneInTalk fontSize="small" />}
                onClick={handleIncomingAccept}
                disabled={isIncomingRecoveryInProgress}
              >
                {t('components:callDetails.controls.incoming.accept')}
              </Button>
              <Button
                id={getElementId('incoming-reject-button')}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<PhoneDisabled fontSize="small" />}
                onClick={handleIncomingReject}
                disabled={!canRejectIncomingOffer || isIncomingRecoveryInProgress}
              >
                {hangupState === 'sending'
                  ? t('components:callDetails.controls.hangup.sending')
                  : t('components:callDetails.controls.incoming.reject')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            id={getElementId('orb-stage')}
            sx={{
              mt: 'auto',
              position: 'relative',
              mb: 'auto',
            }}
          >
            <Box
              id={getElementId('orb-container')}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 180,
                height: 180,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            >
              <Box
                id={getElementId('orb-glow')}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 150,
                  height: 150,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  background: hasMonitorConnection
                    ? `radial-gradient(circle at 30% 30%, ${alpha(
                        theme.palette.primary.light,
                        0.7,
                      )} 0%, ${alpha(theme.palette.primary.main, 0.2)} 45%, transparent 70%)`
                    : `radial-gradient(circle at 30% 30%, ${alpha(
                        theme.palette.grey[300],
                        0.45,
                      )} 0%, ${alpha(theme.palette.grey[700], 0.18)} 45%, transparent 70%)`,
                  filter: `blur(${24 + orbLevel * 34}px)`,
                  opacity: hasMonitorConnection ? orbStyles.glowOpacity : 0.25,
                  transition: 'opacity 0.2s ease',
                }}
                />
              <Box
                id={getElementId('orb-core-wrapper')}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) scale(${orbStyles.scale})`,
                  transition: 'transform 0.12s ease-out',
                }}
              >
                <Box
                  id={getElementId('orb-core')}
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: hasMonitorConnection
                      ? `radial-gradient(circle at 20% 20%, ${alpha(
                          theme.palette.common.white,
                          0.55,
                        )} 0%, ${alpha(theme.palette.primary.light, 0.35)} 30%, ${alpha(
                          theme.palette.primary.main,
                          0.7,
                        )} 70%)`
                      : `radial-gradient(circle at 20% 20%, ${alpha(
                          theme.palette.grey[200],
                          0.5,
                        )} 0%, ${alpha(theme.palette.grey[400], 0.4)} 40%, ${alpha(
                          theme.palette.grey[700],
                          0.6,
                        )} 75%)`,
                    boxShadow: hasMonitorConnection
                      ? `0 0 40px ${alpha(theme.palette.primary.main, 0.35)}`
                      : `0 0 24px ${alpha(theme.palette.grey[700], 0.25)}`,
                    animation: `${orbPulse} ${orbStyles.pulseDuration} ease-in-out infinite`,
                  }}
                >
                  <Box
                    id={getElementId('orb-shimmer')}
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: hasMonitorConnection
                        ? `linear-gradient(120deg, ${alpha(
                            theme.palette.primary.light,
                            0.4,
                          )} 0%, ${alpha(theme.palette.secondary.main, 0.4)} 50%, ${alpha(
                            theme.palette.primary.dark,
                            0.4,
                          )} 100%)`
                        : `linear-gradient(120deg, ${alpha(
                            theme.palette.grey[300],
                            0.32,
                          )} 0%, ${alpha(theme.palette.grey[500], 0.24)} 50%, ${alpha(
                            theme.palette.grey[700],
                            0.28,
                          )} 100%)`,
                      mixBlendMode: 'screen',
                      opacity: hasMonitorConnection ? orbStyles.shimmerOpacity : 0.2,
                    }}
                  />
                </Box>
              </Box>
              {orbStatusIndicators.length > 0 && (
                <>
                  <Box
                    id={getElementId('orb-status-indicators')}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: orbStatusIndicators.length > 2 ? 0.5 : (orbStatusIndicators.length > 1 ? 1 : 0),
                      zIndex: 2,
                      pointerEvents: 'none',
                      animation: `${orbStatusPulse} 2.4s ease-in-out infinite`,
                    }}
                  >
                    {orbStatusIndicators.map((indicator) => (
                      <Tooltip
                        key={indicator.key}
                        title={indicator.label}
                        arrow
                      >
                        <Box
                          sx={{
                            width: orbStatusIndicators.length > 2 ? 30 : (orbStatusIndicators.length > 1 ? 34 : 42),
                            height: orbStatusIndicators.length > 2 ? 30 : (orbStatusIndicators.length > 1 ? 34 : 42),
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: indicator.color,
                            bgcolor: alpha(theme.palette.background.paper, 0.84),
                            border: '1px solid',
                            borderColor: alpha(indicator.color, 0.58),
                            boxShadow: `0 0 22px ${indicator.glowColor}`,
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          {indicator.icon}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                  {orbStatusItems.length > 0 && (
                    <Box
                      id={getElementId('orb-status-text-wrapper')}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, 72px)',
                        zIndex: 2,
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        width: 'min(220px, calc(100vw - 96px))',
                      }}
                    >
                      {orbStatusItems.map((item) => (
                        <Typography
                          key={item.key}
                          id={getElementId(`orb-status-text-${item.key}`)}
                          variant="caption"
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: alpha(theme.palette.background.default, 0.54),
                            border: '1px solid',
                            borderColor: alpha(theme.palette.common.white, 0.08),
                            color: 'text.secondary',
                            fontSize: 11,
                            lineHeight: 1.25,
                            letterSpacing: '0.01em',
                            textAlign: 'center',
                            backdropFilter: 'blur(8px)',
                            boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.16)}`,
                          }}
                        >
                          {item.label}
                          {item.elapsedLabel ? ` · ${item.elapsedLabel}` : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
        {isMonitorActive && (
          <>
            <audio id={getElementId('monitor-audio')} ref={audioRef} />
            <Box
              id={getElementId('volume-control')}
              sx={{
                position: 'absolute',
                right: -6,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                borderRadius: 2,
                px: 1,
                py: 0.75,
                zIndex: 10,
              }}
            >
              <VolumeUpIcon id={getElementId('volume-up-icon')} fontSize="small" />
              <Slider
                id={getElementId('volume-slider')}
                orientation="vertical"
                size="small"
                value={monitorVolume}
                min={0}
                max={1}
                step={0.01}
                onChange={(_, value) => setMonitorVolume(Number(value))}
                sx={{ height: 110 }}
              />
              <VolumeDownIcon id={getElementId('volume-down-icon')} fontSize="small" />
            </Box>
          </>
        )}
        {renderHangupControl()}
        <Box
          id={getElementId('call-status')}
          sx={{
            position: 'absolute',
            left: '20px',
            bottom: '20px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            id={getElementId('call-status-inline')}
            variant="caption"
            sx={{ fontSize: 12, lineHeight: 1.3, whiteSpace: 'nowrap' }}
          >
            {callStatusInlineText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
